// DensityHeatmapCell — one cell of the density heatmap. Its colour is
// normally volume-driven, but an actionable severity OVERRIDES volume
// (brand §2.6): any red activity → red tint+border regardless of
// count; else any amber → amber tint+border; else the four-stop
// brand-violet volume ramp (d-1..d-4). An empty cell carries no tone.
// The today ring and the selected halo are composed on top and stack
// independently of the tone (a cell can be today AND selected AND
// severity-tinted). All tone/ring classes are single-sourced in
// index.css (.sev-red/.sev-amber/.d-1..d-4/.schedule-cell-*).

import type { Activity } from "../../../types/schedule";

interface DensityHeatmapCellProps {
  activities: Activity[];
  isToday: boolean;
  isSelected: boolean;
  onClick: () => void;
  /** Native browser tooltip — typically the human-readable date for
   *  this cell (e.g. "Wed 6 May 2026"). The aria-label still carries
   *  the count + severity for screen readers; the title fills the
   *  mouse-hover gap so users can identify a cell without clicking. */
  title?: string;
}

// Spec §9.2 cellClass: severity beats volume; the brand-severity
// (due-soon) bumps a single-activity day up one volume stop. An empty
// cell carries a faint placeholder background so the grid surface is
// visible at full span (wireframe-3 reading) — without it, empty days
// disappear into the section background and the heatmap reads as
// scattered isolated cells rather than a dense grid.
function toneClass(activities: Activity[]): string {
  if (activities.length === 0) {
    return "bg-slate-100 dark:bg-slate-800/40";
  }
  if (activities.some((a) => a.status === "red")) return "sev-red";
  if (activities.some((a) => a.status === "amber")) return "sev-amber";
  const n = activities.length;
  if (n >= 4) return "d-4";
  if (n === 3) return "d-3";
  if (n === 2) return "d-2";
  return activities.some((a) => a.status === "brand") ? "d-3" : "d-1";
}

function worstSeverityLabel(activities: Activity[]): string {
  if (activities.some((a) => a.status === "red")) return "1+ overdue";
  if (activities.some((a) => a.status === "amber")) return "needs attention";
  if (activities.some((a) => a.status === "brand")) return "due soon";
  return "on track";
}

export default function DensityHeatmapCell({
  activities,
  isToday,
  isSelected,
  onClick,
  title,
}: DensityHeatmapCellProps) {
  const tone = toneClass(activities);
  const rings = [
    isToday ? "schedule-cell-today" : "",
    isSelected ? "schedule-cell-selected" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Colour is never the sole signal — the aria-label carries the
  // count + worst severity for assistive tech (brand §2.6 / R2).
  const label =
    activities.length === 0
      ? "No activities"
      : `${activities.length} ${activities.length === 1 ? "activity" : "activities"}, ${worstSeverityLabel(activities)}`;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={title}
      className={`aspect-square w-full rounded-[3px] transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 ${tone} ${rings}`}
    />
  );
}
